import React, { useEffect, useRef, useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from 'lucide-react';

const Contact: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const message = `Olá! Meu nome é ${formData.name}.%0A%0AEmail: ${formData.email}%0ATelefone: ${formData.phone}%0A%0AMensagem: ${formData.message}`;
    window.open(`https://wa.me/551532415413?text=${encodeURIComponent('Olá! Gostaria de falar com o setor comercial (Gabriel) da Sol Nascente Hortaliças. ' + message)}`, '_blank');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section ref={sectionRef} id="contact" className="section-padding bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="container-custom">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Entre em <span className="text-gradient">Contato</span>
          </h2>
          <p className="text-xl text-primary-300 max-w-3xl mx-auto">
            Estamos prontos para atender você. Fale conosco e tire todas as suas dúvidas
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'scroll-reveal-left visible' : 'scroll-reveal-left'}`}>
            <h3 className="text-3xl font-bold mb-8">Fale Conosco</h3>
            
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="bg-primary-500 p-3 rounded-2xl">
                  <Phone className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Telefone</h4>
                  <p className="text-primary-300">(15) 3241-5413</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="bg-primary-500 p-3 rounded-2xl">
                  <Mail className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Email</h4>
                  <p className="text-primary-300">comercial@solnascentehortalicas.com.br</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="bg-primary-500 p-3 rounded-2xl">
                  <MapPin className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Endereço</h4>
                  <p className="text-primary-300">R. Octávio Lindolfo da Silva <br />  Ibiúna -
SP </p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="bg-primary-500 p-3 rounded-2xl">
                  <Clock className="text-white" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-lg">Horário de Funcionamento</h4>
                  <p className="text-primary-300">Segunda a Sexta: 6h às 18h<br />Sábado: 6h às 12h</p>
                </div>
              </div>
            </div>

            {/* Social/Contact Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <a 
                href="https://wa.me/551532415413" 
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-6 rounded-2xl transition-colors duration-300"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
              <a 
                href="mailto:comercial@solnascentehortalicas.com.br"
                className="flex items-center justify-center gap-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-2xl transition-colors duration-300"
              >
                <Mail size={20} />
                Email
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'scroll-reveal-right visible' : 'scroll-reveal-right'}`}>
            <div className="bg-white rounded-3xl p-8 text-primary-800">
              <h3 className="text-2xl font-bold mb-6">Envie uma Mensagem</h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold mb-2">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-300"
                    placeholder="Seu nome completo"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-300"
                    placeholder="seu@email.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-300"
                    placeholder="(00) 00000-0000"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold mb-2">
                    Mensagem
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all duration-300 resize-none"
                    placeholder="Como podemos ajudar você?"
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-primary-500 hover:bg-primary-600 text-white font-semibold py-4 px-6 rounded-xl transition-colors duration-300 flex items-center justify-center gap-3"
                >
                  <Send size={20} />
                  Enviar via WhatsApp
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className={`mt-16 text-center transition-all duration-1000 delay-600 ${isVisible ? 'scroll-reveal visible' : 'scroll-reveal'}`}>
          <div className="bg-gray-800 rounded-3xl p-8 md:p-12">
            <h3 className="text-3xl font-bold mb-6">
              Atendimento Especializado
            </h3>
            <p className="text-xl text-primary-300 mb-8 max-w-3xl mx-auto">
              Nossa equipe está pronta para oferecer o melhor atendimento e encontrar a solução ideal para suas necessidades
            </p>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-primary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="text-white" size={32} />
                </div>
                <h4 className="font-semibold text-lg mb-2">Resposta Rápida</h4>
                <p className="text-primary-400">Respondemos em até 2 horas</p>
              </div>
              
              <div className="text-center">
                <div className="bg-secondary-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="text-white" size={32} />
                </div>
                <h4 className="font-semibold text-lg mb-2">Suporte 24/7</h4>
                <p className="text-primary-400">Atendimento sempre disponível</p>
              </div>
              
              <div className="text-center">
                <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-white" size={32} />
                </div>
                <h4 className="font-semibold text-lg mb-2">Consultoria Gratuita</h4>
                <p className="text-primary-400">Orientação especializada</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
